/// <reference path="./vite-env.d.ts" />
/**
 * Debug Logger Utility
 *
 * Creates namespaced loggers that only output when VITE_DEBUG is enabled.
 * Set VITE_DEBUG=true in your .env.local for local development.
 *
 * Usage:
 *   import { createDebugLogger } from '@videa/utils';
 *   const debug = createDebugLogger('Upload');
 *   debug.log('Uploading chunk:', chunkIndex);
 *   debug.error('Upload failed:', error);
 */

const isDebugEnabled = (): boolean => {
  try {
    // Check for Vite environment variable
    if (import.meta.env?.VITE_DEBUG === 'true') {
      return true;
    }
    // Also check localStorage for runtime toggling
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('VITE_DEBUG') === 'true';
    }
    return false;
  } catch {
    return false;
  }
};

type LogLevel = 'log' | 'warn' | 'error';

interface DebugLogger {
  log: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

/**
 * Create a namespaced debug logger
 *
 * @param namespace - Prefix for log messages (e.g., 'Upload', 'OPFS', 'SW')
 */
export function createDebugLogger(namespace: string): DebugLogger {
  const createLogFn = (level: LogLevel) => {
    return (...args: unknown[]) => {
      // Always log errors regardless of debug mode
      if (level === 'error' || isDebugEnabled()) {
        // eslint-disable-next-line no-console
        console[level](`[${namespace}]`, ...args);
      }
    };
  };

  return {
    log: createLogFn('log'),
    warn: createLogFn('warn'),
    error: createLogFn('error'),
  };
}
